import { Draggable } from 'react-beautiful-dnd';
import { Switch, Tooltip } from '@mui/material';
import { CustomPopover, useCustomPopoverHook } from '@/components/popover/CustomPopover';
import { DeleteIcon } from '@/components/svg-icons/DeleteIcon';

const ICON_BUTTON = 'flex h-8 w-8 items-center justify-center rounded-lg transition';

/**
 * 에디터 목록의 모듈 카드 공통 틀 (링크·텍스트·구분선·캐러셀·앨범).
 * 끌기 핸들, 종류 배지, 수정·보이기·삭제(확인 팝오버), 카테고리 칩을 같은 모양으로 그린다.
 */
export function ModuleCard({ id, index, isHide, badgeIcon, badgeLabel, badgeExtra, categoryName, onEdit, onToggleHide, onDelete, children, footer }) {
    const deletePopoverHook = useCustomPopoverHook();

    const handleDelete = () => {
        deletePopoverHook.closePopover();
        onDelete(index);
    }

    return (
        <>
            <Draggable draggableId={`SubFdElements-${id}`} index={index}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`mb-3 flex gap-2 rounded-2xl bg-white p-4 pl-2 ring-1 transition-shadow ${snapshot.isDragging ? 'shadow-xl ring-black/10' : 'shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-black/[0.06] hover:shadow-md'}`}
                    >
                        <div
                            {...provided.dragHandleProps}
                            aria-label="순서 바꾸기"
                            className="flex w-6 shrink-0 cursor-grab items-center justify-center self-stretch rounded-lg hover:bg-gray-100 active:cursor-grabbing"
                        >
                            <img src="/assets/icons/dragIndicator_808080.svg" alt="" className="h-5 w-5" />
                        </div>

                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <div className={`flex min-w-0 items-center gap-2 ${isHide ? 'opacity-50' : ''}`}>
                                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--mainColor100)] px-2 py-0.5 text-[11px] font-bold text-[var(--mainColor)]">
                                    {badgeIcon && <img src={badgeIcon} alt="" className="h-3.5 w-3.5" />}
                                    {badgeLabel}
                                </span>
                                {badgeExtra}
                                </div>
                                {isHide && <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">숨김</span>}
                                <div className="ml-auto flex shrink-0 items-center gap-0.5">
                                    <Tooltip title='수정'>
                                        <button type="button" onClick={onEdit} aria-label="수정" className={`${ICON_BUTTON} hover:bg-gray-100`}>
                                            <img src="/assets/icons/edit2_000000.svg" alt="" className="h-4 w-4 opacity-80" />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={isHide ? '숨김 (눌러서 보이기)' : '보임 (눌러서 숨기기)'}>
                                        <Switch size='small' color='success' onChange={() => onToggleHide(index)} checked={!isHide} inputProps={{ 'aria-label': '보이기' }} />
                                    </Tooltip>
                                    <Tooltip title='삭제'>
                                        <button type="button" onClick={(e) => deletePopoverHook.openPopover(e)} aria-label="삭제" className={`${ICON_BUTTON} text-gray-500 hover:bg-red-50 hover:text-[var(--redColor1)]`}>
                                            <DeleteIcon fill="currentColor" width={18} height={18} />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>

                            <div className={`flex flex-col gap-2 ${isHide ? 'opacity-50' : ''}`}>
                                {children}

                                {(footer || categoryName) &&
                                    <div className="flex flex-wrap items-center gap-2">
                                        {footer}
                                        {categoryName && <span className="ml-auto rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-600">{categoryName}</span>}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>

            <CustomPopover anchorEl={deletePopoverHook?.anchorEl} onClose={() => deletePopoverHook?.closePopover()} customLayout>
                <div className="w-60 p-4">
                    <p className="text-sm font-bold text-gray-900">이 모듈을 삭제할까요?</p>
                    <p className="mt-1 text-[13px] text-gray-500">삭제하면 되돌릴 수 없어요.</p>
                    <div className="mt-4 flex justify-end gap-2">
                        <button type="button" onClick={() => deletePopoverHook.closePopover()} className="rounded-lg px-3 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100">취소</button>
                        <button type="button" onClick={handleDelete} className="rounded-lg bg-[var(--redColor1)] px-3 py-2 text-[13px] font-bold text-white hover:opacity-90">삭제</button>
                    </div>
                </div>
            </CustomPopover>
        </>
    );
}
